import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { getLeadByIdFromLocal, updateLeadDetails } from '../hooks/leads';
import { Lead } from '../utils/types';
import {
  LeadHero,
  LeadPrimaryButton,
  LeadSectionCard,
  LeadShell,
} from './leads/components/leadsUi';

export type LeadPatch = {
  title?: string | null;
  first_name?: string | null;
  second_name?: string | null;
  email?: string | null;
  phone?: string | null;
};

export function EditLeadDetailsPage() {
  const toast = useToast();
  const { leadId, orgId } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [formData, setFormData] = useState<Lead | null>(null);
  const [originalFormData, setOriginalFormData] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (orgId && leadId) {
      fetchLead();
    }
  }, [orgId, leadId]);

  const fetchLead = async () => {
    try {
      if (leadId && orgId) {
        const response = await getLeadByIdFromLocal(leadId, orgId);
        if (response.success && response.data) {
          setLead(response.data);
          setFormData(response.data);
          setOriginalFormData(response.data);
        } else {
          setError(new Error('Failed to fetch lead'));
        }
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) =>
      prev ? { ...prev, [name]: value === '' ? null : value } : prev,
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData || !originalFormData || !lead?.id || !orgId) {
      toast({
        title: 'Error',
        description: 'Incomplete lead data.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const updatedFields: LeadPatch = {};
    let hasChanges = false;

    const keys: (keyof LeadPatch)[] = [
      'title',
      'first_name',
      'second_name',
      'email',
      'phone',
    ];

    keys.forEach((key) => {
      const current = formData[key];
      const original = originalFormData[key];
      if (current !== original) {
        updatedFields[key] = current ?? undefined;
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      toast({
        title: 'No changes detected',
        description: 'Update one or more fields before saving.',
        status: 'info',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await updateLeadDetails(orgId, leadId, updatedFields);
      if (!response.success || !response.data) {
        const message =
          typeof response.data === 'string'
            ? response.data
            : 'The lead details could not be updated.';
        throw new Error(message);
      }
      toast({
        title: 'Lead updated',
        description: 'The lead details were saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setFormData(response.data);
      setOriginalFormData(response.data);
      navigate(`/organization/${orgId}/leads/${leadId}`);
    } catch (err: any) {
      setError(err instanceof Error ? err : new Error('An error occurred.'));
      toast({
        title: 'Update failed',
        description: err?.message || err?.response?.data?.detail || 'An error occurred.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(
    () => [
      {
        label: 'Lead name',
        value: `${formData?.first_name || ''} ${formData?.second_name || ''}`.trim() || 'Missing',
      },
      {
        label: 'Email',
        value: formData?.email || 'Missing',
      },
      {
        label: 'Phone',
        value: formData?.phone || 'Missing',
      },
    ],
    [formData],
  );

  if (isLoading || !formData) {
    return (
      <LeadShell compact>
        <LeadSectionCard title="Loading lead" description="Please wait while the lead details are loading.">
          <Text color="gray.500">Loading lead details...</Text>
        </LeadSectionCard>
      </LeadShell>
    );
  }

  return (
    <LeadShell compact>
      <LeadHero
        title="Edit lead"
        description="Update the lead contact details and keep the record accurate for the team."
      />

      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
          <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
            <LeadSectionCard
              title="Contact details"
              description="Update the main lead information used across the dashboard and detail pages."
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    name="title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    borderRadius="16px"
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>First name</FormLabel>
                  <Input
                    name="first_name"
                    value={formData.first_name || ''}
                    onChange={handleChange}
                    borderRadius="16px"
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Last name</FormLabel>
                  <Input
                    name="second_name"
                    value={formData.second_name || ''}
                    onChange={handleChange}
                    borderRadius="16px"
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    borderRadius="16px"
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl gridColumn={{ md: 'span 2' }}>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    borderRadius="16px"
                    bg="gray.50"
                  />
                  <FormHelperText>
                    Keep at least one active contact method on the lead record.
                  </FormHelperText>
                </FormControl>
              </SimpleGrid>
            </LeadSectionCard>
          </Stack>

          <Stack spacing={6}>
            <LeadSectionCard
              title="Save"
              description="Save the updated lead details or go back without making changes."
            >
              <VStack align="stretch" spacing={3}>
                <LeadPrimaryButton
                  type="submit"
                  isLoading={submitting}
                  loadingText="Saving"
                >
                  Save Changes
                </LeadPrimaryButton>
                <Button borderRadius="16px" variant="ghost" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                {error ? (
                  <Text color="red.500" fontSize="sm">
                    {error.message}
                  </Text>
                ) : null}
              </VStack>
            </LeadSectionCard>

            <LeadSectionCard
              title="Current summary"
              description="A quick view of the main lead details."
            >
              <Stack spacing={4}>
                {summary.map((item) => (
                  <Box key={item.label}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color="gray.500"
                      mb={1}
                    >
                      {item.label}
                    </Text>
                    <Text color="gray.900" fontWeight="600">
                      {item.value}
                    </Text>
                  </Box>
                ))}
              </Stack>
            </LeadSectionCard>
          </Stack>
        </SimpleGrid>
      </form>
    </LeadShell>
  );
}
